<template>
  <li
    :class="{
      'issue-card': true,
      [`issue-card--${riskLevel}-risk`]: showRisk,
      'issue-card--responsive': responsive,
      'issue-card--selected': selected,
      'issue-card--auto-height': isSmallCard
    }"
  >
    <div
      :class="{
        'issue-card__content': true,
        'issue-card__content--full-height issue-card__content--border-radius': isSmallCard
      }"
    >
      <ConditionalLink
        :class="{
          'issue-card__detail': true,
          'issue-card__detail--border-radius-left': isSmallCard
        }"
        :to="linkData"
        @click.passive="$emit('select')"
      >
        <!-- Image -->
        <div
          :class="{
            'issue-card__image': true,
            'issue-card__image--border-radius-left': isSmallCard,
            'issue-card__image--center': !image
          }"
        >
          <HideWhileScrolling
            v-if="image"
            :key="issue.id"
          >
            <picture :class="{ 'issue-card__image-fill': isSmallCard }">
              <source
                v-if="responsive"
                media="(min-width: 1016px)"
                :srcset="`${imageUrl({ variant: 'small', format: 'webp', scale: '2x' })} 2x, ${imageUrl({ variant: 'small', format: 'webp' })}`"
                type="image/webp"
              >
              <source
                v-if="responsive"
                media="(min-width: 1016px)"
                :srcset="`${imageUrl({ variant: 'small', format: 'webp', scale: '2x' })} 2x, ${imageUrl({ variant: 'small', format: 'jpeg' })}`"
                type="image/jpeg"
              >
              <source
                :srcset="imageUrl({ variant: 'thumbnail', format: 'webp' })"
                type="image/webp"
              >
              <img :src="imageUrl({ variant: 'thumbnail', format: 'jpeg' })">
            </picture>
            <template #else>
              <div class="issue-card__image-waiting" />
            </template>
          </HideWhileScrolling>
          <div
            v-else
            :class="{ 'issue-card__image-fill issue-card__image-fill--full-height': isSmallCard }"
          >
            <div class="issue-card__image-placeholder" />
          </div>
        </div>
        <!-- Card details -->
        <div class="issue-card__information">
          <div class="issue-card__row">
            <!-- Title -->
            <div class="issue-card__section issue-card__section--inline">
              <div class="issue-card__title typo-small-title">
                {{ title }}
              </div>
              <div
                v-if="issue?.status && !isSmallCard"
                class="issue-card__status hide-for-small show-for-medium show-for-large"
              >
                {{ $t(`inspections.issues.${issue.status}`) }}
              </div>
            </div>
            <!-- Risk rating -->
            <div
              v-if="!isSmallCard"
              class="issue-card__section"
            >
              <div v-if="showRisk" :class="`issue-card__risk ${riskRating?.riskStyle}`">
                {{ riskRating?.riskText }}
              </div>
              <div v-else-if="!responsive" class="issue-card__tag issue-card__tag--tiny typo-small-title">
                <div
                  :class="`issue-card__icon issue-card__icon--no-margin-left issue-card__priority--${issue.priority}`"
                >
                  <UseSvg
                    class="icon icon--tiny"
                    name="triangle_16"
                  />
                </div>
                <div class="issue-card__tag-rating">
                  {{ issue.priority_label ?? issue.priority }}
                </div>
              </div>
            </div>
          </div>
          <!-- Description -->
          <div
            :class="{
              'issue-card__row': true,
              'issue-card__row--reverse-column-priority': !isShaft
            }"
          >
            <div class="issue-card__section issue-card__section--grow">
              <div class="issue-card__description typo-tiny">
                {{
                  showDescription
                    ? issue.description
                    : $t('general.time_ago', { time_span: timeAgo })
                }}
              </div>
            </div>
            <!-- Tags including condition rating and priority -->
            <div
              v-if="tags && (!isShaft || isShaftV2)"
              class="issue-card__section issue-card__section--min-content-width show-for-large"
            >
              <div class="issue-card__tags">
                <!-- Condition -->
                <div class="issue-card__tag typo-small-title">
                  {{ $t('inspections.issues.condition') }}
                  <div
                    :class="`issue-card__icon issue-card__condition--${issue.overall_condition_rating}`"
                  >
                    <UseSvg
                      class="icon icon--small"
                      name="square_16"
                    />
                  </div>
                  <div class="issue-card__tag-rating">
                    {{ issue.overall_condition_rating }}
                  </div>
                </div>
                <!-- Priority -->
                <div class="issue-card__tag typo-small-title">
                  {{ $t('inspections.issues.priority') }}
                  <div
                    :class="`issue-card__icon issue-card__priority--${issue.priority}`"
                  >
                    <UseSvg
                      class="icon icon--small"
                      name="triangle_16"
                    />
                  </div>
                  <div class="issue-card__tag-rating">
                    {{ issue.priority_label ?? issue.priority }}
                  </div>
                </div>
              </div>
            </div>
            <!-- Id -->
            <div
              v-if="!isSmallCard"
              class="issue-card__section"
            >
              <div class="issue-card__id typo-tiny">
                <span>{{ shortId }}</span>
              </div>
              <div
                v-if="issue.location_information && showLocation"
                class="issue-card__location-information typo-tiny text-right show-for-large"
              >
                {{ issue.location_information }}
              </div>
              <!-- Location description -->
              <div
                v-if="issue.location_description && (!isShaft && isShaftV2)"
                class="issue-card__location typo-tiny show-for-large"
              >
                {{ issue.location_description }}
              </div>
              <!-- Template member name -->
              <div
                v-if="issue?.member?.location_description"
                class="issue-card__location typo-tiny show-for-large"
              >
                {{ issue?.member?.location_description }}
              </div>
            </div>
          </div>
          <!-- Data for small cards (issues mainly) -->
          <div
            v-if="isSmallCard && (!isShaft || isShaftV2)"
            :class="{
              'issue-card__row issue-card__row--padding': true,
              'issue-card__row--reverse-column-priority': !isShaft
            }"
          >
            <!-- Tags including condition rating and priority -->
            <div class="issue-card__section">
              <div class="issue-card__tags issue-card__tags--show">
                <!-- Condition -->
                <div class="issue-card__tag issue-card__tag--small typo-small-title">
                  <div
                    :class="`issue-card__icon
                    issue-card__icon--no-margin-left
                    issue-card__condition--${issue.overall_condition_rating}`"
                  >
                    <UseSvg
                      class="icon icon--small"
                      name="square_16"
                    />
                  </div>
                  <div class="issue-card__tag-rating">
                    {{ issue.overall_condition_rating }}
                  </div>
                </div>
                <!-- Priority -->
                <div class="issue-card__tag issue-card__tag--small typo-small-title">
                  <div
                    :class="`issue-card__icon
                    issue-card__icon--no-margin-left
                    issue-card__priority--${issue.priority}`"
                  >
                    <UseSvg
                      class="icon icon--small"
                      name="triangle_16"
                    />
                  </div>
                  <div class="issue-card__tag-rating">
                    {{ issue.priority_label ?? issue.priority }}
                  </div>
                </div>
              </div>
            </div>
            <!-- Risk rating -->
            <div class="issue-card__section">
              <div
                v-if="showRisk"
                :class="`
                issue-card__risk
                issue-card__risk--padding
                issue-card__risk--text-right
                ${riskRating?.riskStyle}`"
              >
                {{ riskRating?.riskText }}
              </div>
            </div>
          </div>
        </div>
      </ConditionalLink>

      <button
        v-if="isLocatable"
        :aria-label="`Locate ${locatorText}`"
        class="issue-card__locator"
        type="button"
        @click="$emit('locate')"
      >
        <div class="issue-card__locator-icon" />
      </button>
    </div>
  </li>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import I18n from 'i18n-js';

// Utils
import { renderShortId } from '@/functions/utils.js';
import getRiskRatingLevel from '@/functions/simm/get_risk_rating_level';
import getRiskRatingLevelForShafts from '@/functions/simm/shafts/get_risk_rating_level';
import { timeAgoInWords } from '@/functions/distance_of_time_in_words';
import useOrganisationProfile from '@/functions/use_organisation_profile';

// Components
import UseSvg from '@/components/generic/use_svg.vue';
import ConditionalLink from '@/components/generic/conditional_link.vue';
import HideWhileScrolling from '@/components/generic/hide_while_scrolling.vue';

const props = defineProps({
  /** Issue item data */
  issue: { type: Object, required: true },
  /** Shows or hides the tags (condition and priority at the bottom of the card) */
  tags: { type: Boolean, default: false },
  /** Changes the card to a small version */
  isSmallCard: { type: Boolean, default: false },
  /** Shows or hides the description */
  showDescription: { type: Boolean, default: true },
  /** Disable or enables the card's responsiveness */
  responsive: { type: Boolean, default: true },
  /** Adds active classes for the selected card */
  selected: { type: Boolean, default: false },
  /** Shows or hides the locator button */
  isLocatable: { type: Boolean, default: false },
  /** Removes the card link to disable routing */
  noLink: Boolean,
  /** Defines the route name */
  overrideIssuePath: { type: String, default: null },
  /** Shows or hides the issue location */
  showLocation: { type: Boolean, default: true },
});
defineEmits(['locate', 'select']);

const route = useRoute();

const organisationProfile = useOrganisationProfile();

const isShaft = computed(() => {
  return route?.path.includes('shafts');
});

const isShaftV2 = computed(() => route?.path.includes('shafts_v2'));

const showRisk = computed(() => {
  if (isShaft.value && !isShaftV2.value) return true
  return organisationProfile.showRisk.value;
});

const operationData = computed(() => {
  if (!route) return;
  if (route?.params.shaftId) {
    if (isShaftV2.value) return { name:'shafts_v2', issue: 'shaftV2Issue', url: `shafts_v2/shafts/${route.params.shaftId}/inspections/${props.issue.inspection_id}/issues/${props.issue.id}` };
    return { name:'shafts', issue: 'shaftIssue', url: `shafts/${route.params.shaftId}/inspections/${props.issue.inspection_id}/issues/${props.issue.id}` };
  } else if (route?.params.shipId) {
    return { name:'ships', issue: 'shipIssue', url: `ships/${route.params.shipId}/inspections/${props.issue.inspection_id}/issues/${props.issue.id}` };
  } else if (route?.params.equipmentId) {
    return { name:'equipment', issue: 'equipmentIssue', url: `equipment/${route.params.equipmentId}/inspections/${props.issue.inspection_id}/issues/${props.issue.id}` };
  } else {
    return { name:'plants', issue: 'issue', url:`operations/${route.params.operationId}/plants/inspections/${props.issue.inspection_id}/issues/${props.issue.id}` };
  }
});

const linkData = computed(() => {
  const nameData = {
    name: props.overrideIssuePath || operationData.value?.issue,
    params: { inspectionId: props.issue.inspection_id, issueId: props.issue.id }
  };
  return props.noLink ? null : { ...{ query: route?.query }, ...nameData };
});

const locatorText = computed(() => {
  return isShaft.value && !isShaftV2.value ? props.issue?.member?.location_display_name : props.issue.title;
});

const riskLevel = computed(() => {
  return isShaft.value ? getRiskRatingLevelForShafts(props.issue.risk_rating) : getRiskRatingLevel(props.issue.risk_rating);
});

const title = computed(() => {
  if (isShaft.value && !isShaftV2.value) return props.issue?.member?.location_display_name;
  return props.issue.title;
});

const riskRating = computed(() => {
  if (isShaft.value && !isShaftV2.value) {
    const validateValue = props?.issue?.overall_condition_rating || props?.issue?.overall_condition_rating === 0;
    const riskText = validateValue ? I18n.t(`condition_percentages.${props?.issue?.overall_condition_rating}`) : '';
    return { riskText: riskText, riskStyle: `issue-card__risk--full-width issue-ratings__condition--${props.issue?.overall_condition_rating}` };
  }
  return { riskText: props.issue.risk_rating, riskStyle: null };
});

const shortId = computed(() => renderShortId(props.issue.id));
const issueBasePath = computed(() => {
  const { organisationId } = route.params;
  return `/organisations/${organisationId}/${operationData.value.url}`;
});
const timeAgo = computed(() => props.issue.inspection?.date ? timeAgoInWords(props.issue.inspection.date) : null);
const image = computed(() => {
  return props.issue?.issue_images?.find((x) => x.attached);
});

function imageUrl(options) {
  if (!image.value && issueBasePath.value) return null;

  let path = `/internal${issueBasePath.value}/issue_images/${image.value.id}.${options.format}?variant=${options.variant}`;
  if (options.scale) path += `&scale=${options.scale}`;
  return path;
}
</script>
